// Execution Logs Page
// Visual debugging interface for AI prompt executions

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ArrowLeft,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  User,
  Zap,
  Timer,
  FileText,
  Filter,
  Search,
  Trash2,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useSettingsNavigationStore } from '@/stores/settingsStore'

// ============================================================================
// HELPERS
// ============================================================================

function formatDateTime(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

// ============================================================================
// TYPES
// ============================================================================

interface ExecutionLog {
  id: string
  workflowId?: string
  workflowKey?: string
  stepId?: string
  stepKey?: string
  promptId?: string
  promptKey: string
  accountId?: string
  projectId?: string
  userId?: string
  triggeredBy: 'user' | 'agent' | 'system' | 'api'
  triggerSource?: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  inputVariables?: Record<string, unknown>
  systemPrompt?: string
  userPrompt?: string
  responseText?: string
  responseJson?: Record<string, unknown>
  model?: string
  temperature?: number
  maxTokens?: number
  inputTokens?: number
  outputTokens?: number
  durationMs?: number
  errorMessage?: string
  errorStack?: string
  startedAt?: string
  completedAt?: string
  createdAt: string
  userName?: string
  userEmail?: string
  promptName?: string
  workflowName?: string
  stepName?: string
}

interface LogStats {
  totalExecutions: number
  completedExecutions: number
  failedExecutions: number
  totalInputTokens: number
  totalOutputTokens: number
  avgDurationMs: number
}

interface FilterOptions {
  promptKeys: string[]
  triggerSources: string[]
  statuses: string[]
  triggeredByOptions: string[]
}

// ============================================================================
// API CLIENT
// ============================================================================

// Always use relative URLs - proxied through Vite (dev) or Caddy (prod)
const API_URL = import.meta.env.VITE_API_URL ?? ''

async function fetchLogs(params: Record<string, string | number | undefined>): Promise<{
  logs: ExecutionLog[]
  total: number
}> {
  const token = localStorage.getItem('ispot-session-token')
  const queryParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      queryParams.append(key, String(value))
    }
  })

  const response = await fetch(`${API_URL}/api/execution-logs?${queryParams}`, {
    credentials: 'include',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch logs')
  }

  return response.json()
}

async function fetchLog(id: string): Promise<{ log: ExecutionLog }> {
  const token = localStorage.getItem('ispot-session-token')

  const response = await fetch(`${API_URL}/api/execution-logs/${id}`, {
    credentials: 'include',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch log')
  }

  return response.json()
}

async function fetchStats(accountId?: string): Promise<{ stats: LogStats }> {
  const token = localStorage.getItem('ispot-session-token')
  const queryParams = accountId ? `?accountId=${accountId}` : ''

  const response = await fetch(`${API_URL}/api/execution-logs/stats${queryParams}`, {
    credentials: 'include',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch stats')
  }

  return response.json()
}

async function fetchFilterOptions(accountId?: string): Promise<FilterOptions> {
  const token = localStorage.getItem('ispot-session-token')
  const queryParams = accountId ? `?accountId=${accountId}` : ''

  const response = await fetch(`${API_URL}/api/execution-logs/filters${queryParams}`, {
    credentials: 'include',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch filter options')
  }

  return response.json()
}

async function deleteAllLogs(accountId?: string): Promise<{ deletedCount: number }> {
  const token = localStorage.getItem('ispot-session-token')
  const queryParams = accountId ? `?accountId=${accountId}` : ''

  const response = await fetch(`${API_URL}/api/execution-logs${queryParams}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  })

  if (!response.ok) {
    throw new Error('Failed to delete logs')
  }

  return response.json()
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatusBadge({ status }: { status: ExecutionLog['status'] }) {
  const config = {
    pending: { icon: Clock, color: 'bg-warm-100 text-warm-700 border-warm-200' },
    running: { icon: Play, color: 'bg-business/10 text-business border-business/20' },
    completed: { icon: CheckCircle, color: 'bg-market/10 text-market border-market/20' },
    failed: { icon: XCircle, color: 'bg-solution/10 text-solution border-solution/20' },
  }

  const { icon: Icon, color } = config[status]

  return (
    <Badge className={`${color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {status}
    </Badge>
  )
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-border-subtle rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 px-4 py-2 bg-warm-50 text-left hover:bg-warm-100 transition-colors"
      >
        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="text-sm font-medium text-text-secondary">{title}</span>
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  )
}

// ============================================================================
// LOG LIST ITEM
// ============================================================================

function LogListItem({
  log,
  isSelected,
  onClick,
}: {
  log: ExecutionLog
  isSelected: boolean
  onClick: () => void
}) {
  const totalTokens = (log.inputTokens || 0) + (log.outputTokens || 0)
  const durationSec = log.durationMs ? (log.durationMs / 1000).toFixed(1) : null

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 border-b border-border-subtle transition-colors ${
        isSelected ? 'bg-business/5 border-l-2 border-l-business' : 'hover:bg-warm-50'
      }`}
    >
      <div className="flex items-start gap-2">
        <div
          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
            log.status === 'completed'
              ? 'bg-market'
              : log.status === 'failed'
              ? 'bg-solution'
              : log.status === 'running'
              ? 'bg-business animate-pulse'
              : 'bg-warm-400'
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-text truncate">
            {log.promptName || log.promptKey}
          </div>
          <div className="text-xs text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
            {log.userName && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {log.userName}
              </span>
            )}
            {log.triggerSource && (
              <span className="text-text-muted">{log.triggerSource}</span>
            )}
          </div>
          <div className="text-xs text-text-muted mt-1 flex items-center gap-3">
            <span>{formatDateTime(new Date(log.createdAt))}</span>
            {totalTokens > 0 && (
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {totalTokens.toLocaleString()} tokens
              </span>
            )}
            {durationSec && (
              <span className="flex items-center gap-1">
                <Timer className="w-3 h-3" />
                {durationSec}s
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

// ============================================================================
// LOG DETAIL VIEW
// ============================================================================

function LogDetailView({
  log,
  onBack,
  onEditPrompt,
}: {
  log: ExecutionLog
  onBack: () => void
  onEditPrompt?: (promptKey: string) => void
}) {
  const totalTokens = (log.inputTokens || 0) + (log.outputTokens || 0)
  const durationSec = log.durationMs ? (log.durationMs / 1000).toFixed(1) : null

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border-subtle bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1 hover:bg-warm-100 rounded transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </button>
            <div>
              <h2 className="font-semibold text-text">
                {log.promptName || log.promptKey}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <StatusBadge status={log.status} />
                <span className="text-xs text-text-muted">
                  {new Date(log.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          {onEditPrompt && log.promptKey && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEditPrompt(log.promptKey)}
              className="flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Edit Prompt
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Context Section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Context</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {log.workflowName && (
              <div className="flex items-center gap-2">
                <span className="text-text-muted w-24">Workflow:</span>
                <span className="text-text">{log.workflowName}</span>
              </div>
            )}
            {log.stepName && (
              <div className="flex items-center gap-2">
                <span className="text-text-muted w-24">Step:</span>
                <span className="text-text">{log.stepName}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-text-muted w-24">Triggered by:</span>
              <span className="text-text capitalize">
                {log.userName || log.triggeredBy}
                {log.triggerSource && ` (${log.triggerSource})`}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Input Variables */}
        {log.inputVariables && Object.keys(log.inputVariables).length > 0 && (
          <CollapsibleSection title="Input Variables" defaultOpen>
            <pre className="text-xs font-mono bg-warm-50 p-3 rounded overflow-x-auto">
              {JSON.stringify(log.inputVariables, null, 2)}
            </pre>
          </CollapsibleSection>
        )}

        {/* System Prompt */}
        {log.systemPrompt && (
          <CollapsibleSection title="System Prompt">
            <pre className="text-xs font-mono whitespace-pre-wrap bg-warm-50 p-3 rounded">
              {log.systemPrompt}
            </pre>
          </CollapsibleSection>
        )}

        {/* User Prompt */}
        {log.userPrompt && (
          <CollapsibleSection title="User Prompt" defaultOpen>
            <pre className="text-xs font-mono whitespace-pre-wrap bg-warm-50 p-3 rounded">
              {log.userPrompt}
            </pre>
          </CollapsibleSection>
        )}

        {/* Response */}
        {log.responseText && (
          <CollapsibleSection title="Response" defaultOpen>
            {log.responseJson ? (
              <pre className="text-xs font-mono bg-warm-50 p-3 rounded overflow-x-auto">
                {JSON.stringify(log.responseJson, null, 2)}
              </pre>
            ) : (
              <pre className="text-xs font-mono whitespace-pre-wrap bg-warm-50 p-3 rounded">
                {log.responseText}
              </pre>
            )}
          </CollapsibleSection>
        )}

        {/* Error */}
        {log.errorMessage && (
          <CollapsibleSection title="Error" defaultOpen>
            <div className="space-y-2">
              <div className="text-sm text-solution font-medium">{log.errorMessage}</div>
              {log.errorStack && (
                <pre className="text-xs font-mono bg-solution/5 p-3 rounded overflow-x-auto text-solution">
                  {log.errorStack}
                </pre>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-text-muted">Model</div>
                <div className="font-medium">{log.model || 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Temperature</div>
                <div className="font-medium">{log.temperature ?? 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Input Tokens</div>
                <div className="font-medium">{log.inputTokens?.toLocaleString() || 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Output Tokens</div>
                <div className="font-medium">{log.outputTokens?.toLocaleString() || 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Total Tokens</div>
                <div className="font-medium">{totalTokens > 0 ? totalTokens.toLocaleString() : 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Duration</div>
                <div className="font-medium">{durationSec ? `${durationSec}s` : 'N/A'}</div>
              </div>
              <div>
                <div className="text-text-muted">Max Tokens</div>
                <div className="font-medium">{log.maxTokens?.toLocaleString() || 'N/A'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ExecutionLogsPage() {
  const currentAccount = useAuthStore((state) => state.currentAccount)
  const [logs, setLogs] = useState<ExecutionLog[]>([])
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null)
  const [stats, setStats] = useState<LogStats | null>(null)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [promptKeyFilter, setPromptKeyFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  // Delete all
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadLogs = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const params: Record<string, string | number | undefined> = {
        limit: 50,
        offset: 0,
      }

      if (currentAccount?.id) {
        params.accountId = currentAccount.id
      }
      if (statusFilter) {
        params.status = statusFilter
      }
      if (promptKeyFilter) {
        params.promptKey = promptKeyFilter
      }
      if (searchQuery) {
        params.search = searchQuery
      }

      const [logsResult, statsResult, filtersResult] = await Promise.all([
        fetchLogs(params),
        fetchStats(currentAccount?.id),
        fetchFilterOptions(currentAccount?.id),
      ])

      setLogs(logsResult.logs)
      setTotal(logsResult.total)
      setStats(statsResult.stats)
      setFilterOptions(filtersResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setIsLoading(false)
    }
  }, [currentAccount?.id, statusFilter, promptKeyFilter, searchQuery])

  const loadLogDetail = useCallback(async (logId: string) => {
    setIsLoadingDetail(true)
    try {
      const result = await fetchLog(logId)
      setSelectedLog(result.log)
    } catch (err) {
      console.error('Failed to load log detail:', err)
    } finally {
      setIsLoadingDetail(false)
    }
  }, [])

  const handleDeleteAll = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteAllLogs(currentAccount?.id)
      setShowDeleteConfirm(false)
      setSelectedLogId(null)
      setSelectedLog(null)
      await loadLogs()
    } catch (err) {
      console.error('Failed to delete all logs:', err)
    } finally {
      setIsDeleting(false)
    }
  }, [currentAccount?.id, loadLogs])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  useEffect(() => {
    if (selectedLogId) {
      loadLogDetail(selectedLogId)
    } else {
      setSelectedLog(null)
    }
  }, [selectedLogId, loadLogDetail])

  const navigateToPrompt = useSettingsNavigationStore((state) => state.navigateToPrompt)

  const handleEditPrompt = (promptKey: string) => {
    // Navigate to workflows tab with prompt selected
    navigateToPrompt(promptKey)
  }

  if (isLoading && logs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading execution logs...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-8 h-8 text-solution mx-auto mb-2" />
          <p className="text-text-muted">{error}</p>
          <Button variant="outline" onClick={loadLogs} className="mt-4">
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex overflow-hidden">
      {/* Left Panel - Stats, Filters & List */}
      <div className="w-[360px] flex-shrink-0 border-r border-border-subtle flex flex-col bg-white">
        {/* Stats Header */}
        {stats && (
          <div className="flex-shrink-0 p-3 border-b border-border-subtle bg-warm-50">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-semibold text-text">{stats.totalExecutions}</div>
                <div className="text-xs text-text-muted">Total</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-market">{stats.completedExecutions}</div>
                <div className="text-xs text-text-muted">Completed</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-solution">{stats.failedExecutions}</div>
                <div className="text-xs text-text-muted">Failed</div>
              </div>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex-shrink-0 p-3 border-b border-border-subtle space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Search prompts, responses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-business/20"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-business/10 border-business' : ''}
            >
              <Filter className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={loadLogs}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={logs.length === 0}
              className="text-solution hover:bg-solution/10 hover:border-solution"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {showFilters && filterOptions && (
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 text-sm border border-border-subtle rounded-lg px-2 py-1.5 bg-warm-50"
              >
                <option value="">All statuses</option>
                {filterOptions.statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <select
                value={promptKeyFilter}
                onChange={(e) => setPromptKeyFilter(e.target.value)}
                className="flex-1 text-sm border border-border-subtle rounded-lg px-2 py-1.5 bg-warm-50"
              >
                <option value="">All prompts</option>
                {filterOptions.promptKeys.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Log List */}
        <div className="flex-1 overflow-y-auto">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-text-muted">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No execution logs yet</p>
              <p className="text-xs mt-1">Logs will appear here when workflows are executed</p>
            </div>
          ) : (
            logs.map((log) => (
              <LogListItem
                key={log.id}
                log={log}
                isSelected={selectedLogId === log.id}
                onClick={() => setSelectedLogId(log.id)}
              />
            ))
          )}
        </div>

        {/* Pagination info */}
        {total > 0 && (
          <div className="flex-shrink-0 px-3 py-2 border-t border-border-subtle text-xs text-text-muted text-center">
            Showing {logs.length} of {total} logs
          </div>
        )}
      </div>

      {/* Right Panel - Detail View */}
      <div className="flex-1 bg-warm-50 overflow-hidden">
        {isLoadingDetail ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
          </div>
        ) : selectedLog ? (
          <LogDetailView
            log={selectedLog}
            onBack={() => setSelectedLogId(null)}
            onEditPrompt={handleEditPrompt}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-text-muted">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a log entry to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-text mb-2">Delete All Logs</h3>
            <p className="text-sm text-text-muted mb-4">
              Are you sure you want to delete all {total} execution logs? This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAll}
                disabled={isDeleting}
                className="bg-solution hover:bg-solution/90"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExecutionLogsPage
